if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      if (!this.form) return;
      const variantInput = this.form.querySelector('[name=id]');
      if (variantInput) variantInput.disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.eventContext = this.form.dataset.eventContext;
      this.hideErrors = this.dataset.hideErrors === 'true';
    }

    submitFrom(opener) {
      const button = this.form?.querySelector('[name="add"]');
      if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true' || button.classList.contains('loading')) return;
      this.submitOpener = opener;
      try {
        this.form.requestSubmit(button);
      } finally {
        this.submitOpener = null;
      }
    }

    async onSubmitHandler(event) {
      const minicart = document.querySelector('mini-cart');
      if (!minicart?.add) return; // Native form submission remains the fallback.
      if (event.submitter?.name === 'checkout' || event.submitter?.closest('.shopify-payment-button')) return;
      event.preventDefault();
      const submitButton = event.submitter || this.querySelector('[type="submit"]');
      if (!submitButton || submitButton.disabled || submitButton.classList.contains('loading')) return;
      this.handleErrorMessage();
      const formData = new FormData(this.form);
      const spinner = submitButton.querySelector('.button-overlay-spinner');
      submitButton.disabled = true;
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.classList.add('loading');
      spinner?.classList.remove('hidden');
      try {
        await minicart.add(formData, this.submitOpener || submitButton, this.eventContext || 'product-form');
      } catch (error) {
        this.handleErrorMessage(error.message);
        publish(PUB_SUB_EVENTS.cartError, { source: 'product-form', productVariantId: formData.get('id'), message: error.message, errors: error.message });
      } finally {
        // A variant response may have made this same button unavailable while adding.
        if (this.form.querySelector('[name="id"]')?.value === formData.get('id')) {
          submitButton.disabled = false;
        }
        if (submitButton.disabled) submitButton.setAttribute('aria-disabled', 'true');
        else submitButton.removeAttribute('aria-disabled');
        submitButton.classList.remove('loading');
        spinner?.classList.add('hidden');
      }
    }

    handleErrorMessage(message = '') {
      if (this.hideErrors) return;
      const wrapper = this.querySelector('.product-form-error-message-wrapper');
      const text = wrapper?.querySelector('.product-form-error-message');
      if (wrapper && text) { wrapper.hidden = !message; text.textContent = message; }
      else if (message) { const cart = document.querySelector('mini-cart'); cart?.open(); cart?.showError(message); }
    }
  });
}

(() => {
  const ALL_VARIANTS_CONTAINER_SELECTOR = "[data-all-variants-container]";
  const FALLBACK_VALUE = "—";

  const toCleanString = (value) => {
    if (typeof value === "string") {
      return value.trim();
    }

    if (value == null) {
      return "";
    }

    return String(value).trim();
  };

  const findVariantPickerContainer = (container) => {
    let sibling = container.nextElementSibling;
    while (sibling) {
      if (sibling.classList && sibling.classList.contains("product-variants")) {
        return sibling;
      }
      sibling = sibling.nextElementSibling;
    }

    return container.parentElement?.querySelector(".product-variants") ?? null;
  };

  const hideOriginalPickers = (pickerContainer) => {
    if (!pickerContainer) {
      return;
    }

    pickerContainer.classList.add("hidden");
    pickerContainer.setAttribute("aria-hidden", "true");
  };

  const componentCurrentVariantId = (variantElement) => {
    if (!variantElement || !variantElement.currentVariant) {
      return null;
    }

    return variantElement.currentVariant.id ?? null;
  };

  const updateVariantChips = (sectionId, record) => {
    const mediaChip = document.querySelector(
      `[data-media-condition-chip][data-section-id="${sectionId}"]`,
    );
    const sleeveChip = document.querySelector(
      `[data-sleeve-condition-chip][data-section-id="${sectionId}"]`,
    );

    const mediaValue = record ? record.mediaCondition || FALLBACK_VALUE : FALLBACK_VALUE;
    const sleeveValue = record ? record.sleeveCondition || FALLBACK_VALUE : FALLBACK_VALUE;

    if (mediaChip) {
      const valueNode = mediaChip.querySelector(".product-variant-chip-value");
      if (valueNode) {
        valueNode.textContent = mediaValue;
      }
      mediaChip.hidden = false;
    }

    if (sleeveChip) {
      const valueNode = sleeveChip.querySelector(".product-variant-chip-value");
      if (valueNode) {
        valueNode.textContent = sleeveValue;
      }
      sleeveChip.hidden = false;
    }
  };

  const updateHiddenVariantInput = (hiddenInput, record) => {
    if (!hiddenInput) {
      return;
    }

    if (record) {
      hiddenInput.value = record.idString;
      hiddenInput.disabled = false;
    } else {
      hiddenInput.value = "";
      hiddenInput.disabled = true;
    }
  };

  const getInitialVariantId = (variantMap, sectionId, hiddenInput) => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariantId = urlParams.get("variant");
    if (urlVariantId && variantMap.has(urlVariantId)) {
      return urlVariantId;
    }

    const formVariantId = hiddenInput?.value ? String(hiddenInput.value) : null;
    if (formVariantId && variantMap.has(formVariantId)) {
      return formVariantId;
    }

    const selectedVariant = document
      .getElementById(`ProductPage-${sectionId}`)
      ?.querySelector("[data-variant-id]");
    const selectedVariantId = selectedVariant?.dataset.variantId;
    if (selectedVariantId && variantMap.has(selectedVariantId)) {
      return selectedVariantId;
    }

    return null;
  };

  const initializeContainer = (container) => {
    if (!container || container.dataset.allVariantsInitialized === "true") {
      return;
    }

    const sectionId = container.dataset.sectionId;
    const selectElement = container.querySelector("[data-all-variants-select]");
    if (!sectionId || !selectElement) {
      container.hidden = true;
      container.dataset.allVariantsInitialized = "true";
      return;
    }

    const script = document.querySelector(
      `script[data-all-variants-extended][data-section-id="${sectionId}"]`,
    );
    if (!script) {
      container.hidden = true;
      container.dataset.allVariantsInitialized = "true";
      return;
    }

    let extendedData;
    try {
      extendedData = JSON.parse(script.textContent);
    } catch (error) {
      console.error("[All variants dropdown] Failed to parse extended variant data.", error);
      container.hidden = true;
      container.dataset.allVariantsInitialized = "true";
      return;
    }

    const rawVariants = Array.isArray(extendedData?.variants) ? extendedData.variants : [];
    const variantMap = new Map();
    const inStockVariants = [];
    const allVariants = [];

    // Respect section-level control for out of stock handling
    const variantPickerContainer = findVariantPickerContainer(container);
    const variantElement =
      variantPickerContainer?.querySelector("variant-selects, variant-radios") ?? null;
    const outOfStockVariantsSetting =
      container.dataset.outOfStockVariants ||
      (variantElement?.dataset?.variantsVisibility ?? "show");

    rawVariants.forEach((variant) => {
      if (!variant) {
        return;
      }

      const format = toCleanString(variant.format);
      const providedLabel = toCleanString(variant.label);
      if (!format && !providedLabel) {
        console.warn(
          `[All variants dropdown] Missing evp.format metafield for variant ${variant.id}.`,
        );
        return;
      }

      // Per Shopify docs, 'available' reflects sellability (inventory quantities + policy)
      const isOutOfStock = !Boolean(variant.available);
      const pressing = toCleanString(variant.pressing);
      const mediaCondition = toCleanString(variant.mediaCondition);
      const sleeveCondition = toCleanString(variant.sleeveCondition);

      const record = {
        id: variant.id,
        idString: String(variant.id),
        available: Boolean(variant.available),
        inventoryManagement: variant.inventory_management,
        inventoryQuantity: Number(variant.inventory_quantity ?? 0),
        variantData: variant.variant_data || null,
        format,
        formatUpper: format.toUpperCase(),
        pressing,
        mediaCondition,
        sleeveCondition,
        options: Array.isArray(variant.options) ? variant.options : [],
        isOutOfStock,
      };

      record.label =
        providedLabel ||
        `${record.formatUpper} / ${record.pressing || FALLBACK_VALUE} / ${
          record.mediaCondition || FALLBACK_VALUE
        } / ${record.sleeveCondition || FALLBACK_VALUE}`;

      variantMap.set(record.idString, record);
      allVariants.push(record);
      if (!isOutOfStock) {
        inStockVariants.push(record);
      }
    });

    const productForm = document.getElementById(`product-form-${sectionId}`);
    const hiddenInput = productForm?.querySelector('[name="id"]') ?? null;
    const addToCartButton = productForm?.querySelector('[name="add"]') ?? null;
    const soldOutLabel = container.dataset.soldOutLabel || "Sold out";
    const soldOutLabelPrefix = `[${String(soldOutLabel).toUpperCase()}]`;
    let originalPickersHidden = false;

    const applyVariantSelection = (record, { updateSelect = false, skipFetch = false } = {}) => {
      if (updateSelect && record && selectElement.value !== record.idString) {
        selectElement.value = record.idString;
      }

      if (!record) {
        updateVariantChips(sectionId, null);
        updateHiddenVariantInput(hiddenInput, null);
        variantElement?.setProductBenefits?.(null);

        if (addToCartButton) {
          addToCartButton.disabled = true;
          addToCartButton.setAttribute("aria-disabled", "true");
          addToCartButton.closest(".product-actions-add-to-cart")?.classList.add(
            "product-actions-add-to-cart-disabled",
          );
        }

        if (!originalPickersHidden) {
          hideOriginalPickers(variantPickerContainer);
          originalPickersHidden = true;
        }

        return;
      }

      updateVariantChips(sectionId, record);
      updateHiddenVariantInput(hiddenInput, record);

      let shouldFetch = !skipFetch;
      if (
        shouldFetch &&
        variantElement &&
        record.variantData &&
        componentCurrentVariantId(variantElement) === record.variantData.id
      ) {
        shouldFetch = false;
      }

      if (shouldFetch) {
        if (variantElement && record.variantData) {
          const component = variantElement;
          component.currentVariant = record.variantData;
          component.hideVariants = component.dataset.hideVariants === "true";
          component.updateVariantInput();
          component.updateMedia();
          component.toggleAddButton(true);

          const targetUrl = component.dataset.url || window.location.pathname;
          const shouldFetchFullPage = component.dataset.updateUrl === "true";
          const productUrl = component.getProductInfoUrl(
            targetUrl,
            record.variantData.id,
            shouldFetchFullPage,
          );

          component.updateURL(targetUrl);
          component.updateShareUrl(targetUrl);
          component.renderProductInfo(
            productUrl,
            selectElement.id,
            component.handleUpdateProductInfo(component.sectionId).bind(component),
          );
        } else if (variantElement && !record.variantData) {
          console.warn(
            `[All variants dropdown] Missing full variant data for ${record.idString}.`,
          );
        } else if (addToCartButton) {
          const addToCartWrapper = addToCartButton.closest(".product-actions-add-to-cart");
          addToCartButton.disabled = !record.available;
          addToCartButton.toggleAttribute("aria-disabled", !record.available);
          addToCartWrapper?.classList.toggle(
            "product-actions-add-to-cart-disabled",
            !record.available,
          );
        }
      }

      if (!originalPickersHidden) {
        hideOriginalPickers(variantPickerContainer);
        originalPickersHidden = true;
      }
    };

    selectElement.innerHTML = "";

    const baseVariantsToShow =
      outOfStockVariantsSetting === "hide" ? inStockVariants : allVariants;
    const variantsToShow =
      outOfStockVariantsSetting === "hide"
        ? baseVariantsToShow
        : [
            ...baseVariantsToShow.filter((variant) => !variant.isOutOfStock),
            ...baseVariantsToShow.filter((variant) => variant.isOutOfStock),
          ];

    if (!variantsToShow.length) {
      const soldOutOption = document.createElement("option");
      soldOutOption.value = "";
      soldOutOption.textContent = soldOutLabel;
      soldOutOption.disabled = true;
      soldOutOption.selected = true;
      selectElement.appendChild(soldOutOption);
      selectElement.disabled = true;

      applyVariantSelection(null, { skipFetch: true });
      if (addToCartButton) {
        const addToCartWrapper = addToCartButton.closest(".product-actions-add-to-cart");
        addToCartButton.disabled = true;
        addToCartButton.setAttribute("aria-disabled", "true");
        addToCartWrapper?.classList.add("product-actions-add-to-cart-disabled");
      }
      hideOriginalPickers(variantPickerContainer);
      container.hidden = false;

      container.dataset.allVariantsInitialized = "true";
      return;
    }

    variantsToShow.forEach((record) => {
      const option = document.createElement("option");
      option.value = record.idString;

      if (record.isOutOfStock) {
        option.textContent = `${soldOutLabelPrefix} ${record.label}`;
      } else {
        option.textContent = record.label;
      }

      if (record.isOutOfStock && outOfStockVariantsSetting === "disable") {
        option.disabled = true;
      }

      selectElement.appendChild(option);
    });

    selectElement.disabled = false;

    let initialVariantId = getInitialVariantId(variantMap, sectionId, hiddenInput);
    let initialRecord = initialVariantId ? variantMap.get(initialVariantId) : null;

    const isRendered =
      initialRecord && variantsToShow.some((variant) => variant.idString === initialRecord.idString);
    const shouldAvoidOutOfStock =
      initialRecord && initialRecord.isOutOfStock && outOfStockVariantsSetting === "hide";

    if (!isRendered || shouldAvoidOutOfStock) {
      const fallback =
        inStockVariants[0] ||
        (outOfStockVariantsSetting === "show" || outOfStockVariantsSetting === "disable"
          ? variantsToShow[0]
          : null);
      initialRecord = fallback || null;
      initialVariantId = initialRecord?.idString || null;
    }

    if (initialRecord) {
      selectElement.value = initialRecord.idString;
    } else {
      selectElement.value = "";
    }
    applyVariantSelection(initialRecord);
    container.hidden = false;

    selectElement.addEventListener("change", (event) => {
      const variantId = event.target.value;
      const record = variantMap.get(variantId);

      if (!record) {
        applyVariantSelection(null);
        return;
      }

      if (record.isOutOfStock && outOfStockVariantsSetting === "hide") {
        const fallback =
          inStockVariants[0] ||
          (outOfStockVariantsSetting === "show" || outOfStockVariantsSetting === "disable"
            ? variantsToShow[0]
            : null);
        if (fallback) {
          selectElement.value = fallback.idString;
          applyVariantSelection(fallback);
        } else {
          selectElement.value = "";
          applyVariantSelection(null);
        }
        return;
      }

      applyVariantSelection(record);
    });

    let unsubscribeVariantChange = null;
    if (
      typeof subscribe === "function" &&
      typeof PUB_SUB_EVENTS === "object" &&
      PUB_SUB_EVENTS.productVariantChange
    ) {
      unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.productVariantChange, (payload) => {
        if (!document.contains(container)) {
          unsubscribeVariantChange?.();
          return;
        }

        if (!payload || payload.sectionId !== sectionId) {
          return;
        }

        const variantId = payload.variant?.id;
        if (!variantId) {
          applyVariantSelection(null, { skipFetch: true });
          return;
        }

        const key = String(variantId);
        const record = variantMap.get(key);
        if (!record) {
          applyVariantSelection(null, { skipFetch: true });
          return;
        }

        if (record.isOutOfStock && outOfStockVariantsSetting === "hide") {
          const fallback =
            inStockVariants[0] ||
            (outOfStockVariantsSetting === "show" || outOfStockVariantsSetting === "disable"
              ? variantsToShow[0]
              : null);
          if (fallback) {
            applyVariantSelection(fallback, { updateSelect: true, skipFetch: true });
          } else {
            selectElement.value = "";
            applyVariantSelection(null, { skipFetch: true });
          }
          return;
        }

        applyVariantSelection(record, { updateSelect: true, skipFetch: true });
      });
    }

    container.dataset.allVariantsInitialized = "true";
  };

  const initializeAllContainers = (sectionId = null) => {
    const selector = sectionId
      ? `${ALL_VARIANTS_CONTAINER_SELECTOR}[data-section-id="${sectionId}"]`
      : ALL_VARIANTS_CONTAINER_SELECTOR;
    const containers = document.querySelectorAll(selector);

    containers.forEach((container) => {
      initializeContainer(container);
    });
  };

  const onReady = () => {
    initializeAllContainers();
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", onReady);
  } else {
    onReady();
  }

  document.addEventListener("shopify:section:load", (event) => {
    const sectionId = event?.detail?.sectionId;
    if (sectionId) {
      initializeAllContainers(sectionId);
    }
  });

  if (
    typeof subscribe === "function" &&
    typeof PUB_SUB_EVENTS === "object" &&
    PUB_SUB_EVENTS.sectionRefreshed
  ) {
    subscribe(PUB_SUB_EVENTS.sectionRefreshed, (event) => {
      const sectionId = event?.data?.sectionId;
      if (sectionId) {
        requestAnimationFrame(() => {
          initializeAllContainers(sectionId);
        });
      }
    });
  }
})();
